/**
 * Makes list editing behave the way authors expect, where Lexical's defaults don't.
 *
 * 1. Tab / Shift+Tab nest and un-nest the current list item.
 *    Lexical's TabIndentationPlugin only indents when the caret sits at the very
 *    start of the block; anywhere else it inserts a literal tab character. Inside
 *    a list that reads as "Tab is broken" — the item stays put and an invisible
 *    tab is added to its text (which the AST conversion then drops, so the editor
 *    and the saved document disagree). The toolbar advertises the expected
 *    behaviour already ("Increase Indent (Tab)"). Registered above
 *    TabIndentationPlugin (COMMAND_PRIORITY_LOW > COMMAND_PRIORITY_EDITOR), so it
 *    claims Tab inside list items and leaves every other block to Lexical.
 *
 * 2. List commands keep working after the document is emptied.
 *    Selecting all and deleting leaves the selection anchored on the root rather
 *    than inside the surviving empty paragraph, and Lexical's insertList ignores
 *    such a selection entirely — the bullet button and ⌘⇧8 just stop responding.
 *    This re-anchors the caret into the block before the list command runs, then
 *    returns false so @lexical/list handles the command as usual.
 */

import {
  $isListItemNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $findMatchingParent, mergeRegister } from '@lexical/utils'
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical'
import { useEffect } from 'react'

/**
 * Move a selection parked on the root down into the blocks it covers. Always
 * returns false so the command carries on to its real handler.
 *
 * A root-anchored selection also survives the list insert, so the keystroke after
 * it replaces the whole document instead of typing into the new item — which is
 * why this runs before the command rather than repairing things afterwards.
 */
function $reanchorRootSelection(): false {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return false
  }

  const root = selection.anchor.getNode()
  if (!$isRootOrShadowRoot(root) || !$isElementNode(root)) {
    return false
  }

  const lastIndex = root.getChildrenSize() - 1
  if (lastIndex < 0) {
    return false
  }

  const clamp = (index: number) => Math.max(0, Math.min(index, lastIndex))

  if (selection.isCollapsed()) {
    const block = root.getChildAtIndex(clamp(selection.anchor.offset))
    if ($isElementNode(block)) {
      block.selectEnd()
    }
    return false
  }

  // An element selection's offsets are child indices, the focus being one past
  // the last covered block.
  const offsets = [selection.anchor.offset, selection.focus.offset]
  const first = root.getChildAtIndex(clamp(Math.min(...offsets)))
  const last = root.getChildAtIndex(clamp(Math.max(...offsets) - 1))
  if (!$isElementNode(first) || !$isElementNode(last)) {
    return false
  }

  first.selectStart()
  const started = $getSelection()
  if (!$isRangeSelection(started)) {
    return false
  }
  const start = { key: started.anchor.key, offset: started.anchor.offset, type: started.anchor.type }

  last.selectEnd()
  const ended = $getSelection()
  if ($isRangeSelection(ended)) {
    ended.anchor.set(start.key, start.offset, start.type)
  }
  return false
}

export function ListEditingPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) {
            return false
          }

          const inList = [selection.anchor, selection.focus].every((point) =>
            Boolean($findMatchingParent(point.getNode(), $isListItemNode)),
          )
          if (!inList) {
            return false
          }

          event.preventDefault()
          editor.dispatchCommand(event.shiftKey ? OUTDENT_CONTENT_COMMAND : INDENT_CONTENT_COMMAND, undefined)
          return true
        },
        COMMAND_PRIORITY_LOW,
      ),
      ...[INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND].map((command) =>
        editor.registerCommand(command, $reanchorRootSelection, COMMAND_PRIORITY_HIGH),
      ),
    )
  }, [editor])

  return null
}

export default ListEditingPlugin
